import { useCallback, useEffect, useState } from "react";
import Modal from "../utils/Modal";
import apiClient from "../api/apiClient";

const FIELD_LABELS = {
  baseFare: "Base Fare (₹)",
  perKmFare: "Per Km Fare (₹)",
  rainCharge: "Rain Charge (₹)",
  nightCharge: "Night Charge (₹)",
  platformCharge: "Platform Charge (₹)",
  highDemandCharge: "High Demand Charge (₹)",
  commissionPercentage: "Commission (%)",
  gstPercentage: "GST (%)",
};

// frontend display only
const getVehicleDisplayName = (vehicleInfo) => {
  switch (vehicleInfo) {
    case "FOUR_WHEELER_EV":
      return "8ft Pickup";
    case "FOUR_WHEELER_PETROL":
      return "10ft Pickup";
    case "FOUR_WHEELER_CNG":
      return "10ft Pickup EV";

    case "THREE_WHEELER_EV":
      return "Auto (3W)";
    case "THREE_WHEELER_PETROL":
      return "Open Loader EV (3W)";
    case "THREE_WHEELER_CNG":
      return "Open Loader (3W)";

    case "TWO_WHEELER_EV":
      return "2 Wheeler EV";
    case "TWO_WHEELER_PETROL":
      return "2 Wheeler Petrol";

    default:
      return vehicleInfo;
  }
};

export default function VehicleCharges() {
  const [vehicles, setVehicles] = useState([]);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState({});
  const token = localStorage.getItem("token");

  const fetchVehiclePricing = useCallback(async () => {
    const res = await apiClient.get("/admin/vehicle-pricing", {
      headers: { Authorization: `Bearer ${token}` },
    });
    setVehicles(res.data);
  }, [token]);

  useEffect(() => {
    fetchVehiclePricing();
  }, [fetchVehiclePricing]);

  const toggleActive = async (vehicleInfo, active) => {
    await apiClient.put(
      `/admin/${vehicleInfo}/active?active=${active}`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );

    setVehicles((prev) =>
      prev.map((v) => (v.vehicleInfo === vehicleInfo ? { ...v, active } : v))
    );
  };

  const openEdit = (row) => {
    setEditRow(row);
    setForm({
      baseFare: row.baseFare,
      perKmFare: row.perKmFare,
      rainCharge: row.rainCharge,
      nightCharge: row.nightCharge,
      platformCharge: row.platformCharge,
      highDemandCharge: row.highDemandCharge,
      commissionPercentage: row.commissionPercentage,
      gstPercentage: row.gstPercentage,
      isActive: row.active,
    });
  };

  const saveEdit = async () => {
    await apiClient.patch(`/admin/${editRow.vehicleInfo}`, form, {
      headers: { Authorization: `Bearer ${token}` },
    });

    fetchVehiclePricing();
    setEditRow(null);
  };

  return (
    <>
      <h2 className="page-title">Vehicle Charges</h2>

      <div className="vc-wrapper">
        <table className="vc-table">
          <colgroup>
            <col className="vc-col-vehicle" />
            <col className="vc-col-fare" />
            <col className="vc-col-perkm" />
            <col className="vc-col-platform" />
            <col className="vc-col-commission" />
            <col className="vc-col-gst" />
            <col className="vc-col-status" />
            <col className="vc-col-action" />
          </colgroup>

          <thead>
            <tr>
              <th>Vehicle</th>
              <th>Base Fare</th>
              <th>Per Km</th>
              <th>Platform</th>
              <th>Commission</th>
              <th>GST</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {vehicles.map((v) => (
              <tr key={v.id}>
                <td>{getVehicleDisplayName(v.vehicleInfo)}</td>
                <td>₹{v.baseFare}</td>
                <td>₹{v.perKmFare}</td>
                <td>₹{v.platformCharge}</td>
                <td>{v.commissionPercentage}%</td>
                <td>
                  {v.gstPercentage}%
                  <small style={{ display: "block", fontSize: "11px" }}>
                    Fixed as per Govt. rules
                  </small>
                </td>

                <td>
                  <span className={v.active ? "status active" : "status blocked"}>
                    {v.active ? "Active" : "Inactive"}
                  </span>
                </td>

                <td className="vc-actions">
                  <button
                    className={`btn ${v.active ? "block" : "unblock"}`}
                    onClick={() => toggleActive(v.vehicleInfo, !v.active)}
                  >
                    {v.active ? "Deactivate" : "Activate"}
                  </button>

                  <button className="btn edit" onClick={() => openEdit(v)}>
                    Edit Price
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editRow && (
        <Modal
          title={`Edit Pricing - ${getVehicleDisplayName(editRow.vehicleInfo)}`}
          onClose={() => setEditRow(null)}
        >
          <div className="edit-form">
            {Object.keys(form).map(
              (key) =>
                key !== "isActive" && (
                  <div className="form-group" key={key}>
                    <label>{FIELD_LABELS[key] || key}</label>

                    {key === "gstPercentage" && (
                      <small style={{ display: "block", marginBottom: "6px" }}>
                        GST is fixed as per Govt. rules. It will be changed by developer only 😊.
                      </small>
                    )}

                    <input
                      type="number"
                      value={form[key]}
                      disabled={key === "gstPercentage"}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          [key]: Number(e.target.value),
                        })
                      }
                    />
                  </div>
                )
            )}
          </div>

          <button className="btn-primary" onClick={saveEdit}>
            Save Changes
          </button>
        </Modal>
      )}
    </>
  );
}